import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { environment } from '../../../../../environments/environment';
import { ParentButtonComponent } from '../../../../shared/components/ui/parent-button/parent-button.component';
import { IdentityConsentCardComponent } from '../identity-consent-card/identity-consent-card.component';
import {
  KycMockStep,
  IDENTITY_CONSENT_TERMS_VERSION,
} from '../../models/identity-verification.model';
import { KidVerificationService } from '../../services/kid-verification.service';
import { StepUpPasswordService } from '../../services/step-up-password.service';

@Component({
  selector: 'app-kid-document-verify',
  standalone: true,
  imports: [
    CommonModule,
    TranslatePipe,
    ParentButtonComponent,
    IdentityConsentCardComponent,
  ],
  templateUrl: './kid-document-verify.component.html',
  styleUrl: './kid-document-verify.component.scss',
})
export class KidDocumentVerifyComponent implements OnInit {
  #kidVerification = inject(KidVerificationService);
  #stepUp = inject(StepUpPasswordService);

  kidId = input.required<string>();
  kidFirstName = input('');
  kidLastName = input('');
  kidBirthDate = input('');

  completed = output<void>();
  cancelled = output<void>();

  readonly step = signal<KycMockStep>('intro');
  readonly documentType = signal<'passport' | 'id_card' | 'birth_certificate'>(
    'birth_certificate',
  );
  readonly consentAccepted = signal(false);
  readonly isBusy = signal(false);
  readonly simulateFailed = signal(false);
  readonly verificationFailed = signal(false);

  readonly showSimulate = computed(() => !environment.production);

  async ngOnInit(): Promise<void> {
    try {
      await this.#kidVerification.loadSession(this.kidId());
      if (this.step() === 'intro') {
        this.step.set('consent');
      }
    } catch {
      // No active session yet.
    }
  }

  async startVerification(): Promise<void> {
    this.step.set('consent');
  }

  async goToDocumentType(): Promise<void> {
    if (!this.consentAccepted() || this.isBusy()) {
      return;
    }

    const stepUp = await this.#stepUp.prompt({
      titleKey: 'app.identity.stepUp.kidKycTitle',
      bodyKey: 'app.identity.stepUp.kidKycBody',
      confirmKey: 'app.identity.stepUp.confirm',
    });
    if (!stepUp) {
      return;
    }

    this.isBusy.set(true);
    try {
      await this.#kidVerification.createSession(this.kidId(), {
        consentAccepted: true,
        termsVersion: IDENTITY_CONSENT_TERMS_VERSION,
        password: stepUp.password,
        totpCode: stepUp.totpCode,
      });
      this.step.set('documentType');
    } finally {
      this.isBusy.set(false);
    }
  }

  selectDocumentType(type: 'passport' | 'id_card' | 'birth_certificate'): void {
    this.documentType.set(type);
    this.step.set('documentFront');
  }

  nextFromDocumentFront(): void {
    this.step.set(
      this.documentType() === 'birth_certificate' ? 'selfie' : 'documentBack',
    );
  }

  nextFromDocumentBack(): void {
    this.step.set('selfie');
  }

  async completeCapture(): Promise<void> {
    if (this.isBusy()) {
      return;
    }
    this.step.set('processing');
    this.isBusy.set(true);
    this.verificationFailed.set(false);

    await new Promise((resolve) => setTimeout(resolve, 1800));

    try {
      if (this.showSimulate()) {
        await this.ensureActiveSession();
        const result = await this.#kidVerification.simulateVerification(
          this.kidId(),
          {
            success: !this.simulateFailed(),
            documentCountry: 'NL',
            birthDate: this.kidBirthDate(),
            firstName: this.kidFirstName(),
            lastName: this.kidLastName(),
          },
        );
        this.simulateFailed.set(false);
        this.verificationFailed.set(!result.verified);
        this.step.set('result');
        if (result.verified) {
          this.completed.emit();
        }
        return;
      }

      this.verificationFailed.set(true);
      this.step.set('result');
    } catch {
      this.verificationFailed.set(true);
      this.step.set('result');
    } finally {
      this.isBusy.set(false);
    }
  }

  async retry(): Promise<void> {
    this.verificationFailed.set(false);
    this.isBusy.set(true);
    try {
      await this.ensureActiveSession();
      this.step.set('documentType');
    } catch {
      this.step.set('consent');
    } finally {
      this.isBusy.set(false);
    }
  }

  private async ensureActiveSession(): Promise<void> {
    const stepUp = await this.#stepUp.prompt({
      titleKey: 'app.identity.stepUp.kidKycTitle',
      bodyKey: 'app.identity.stepUp.kidKycBody',
      confirmKey: 'app.identity.stepUp.confirm',
    });
    if (!stepUp) {
      throw new Error('step_up_cancelled');
    }
    await this.#kidVerification.createSession(this.kidId(), {
      consentAccepted: true,
      termsVersion: IDENTITY_CONSENT_TERMS_VERSION,
      password: stepUp.password,
      totpCode: stepUp.totpCode,
    });
  }

  goBack(): void {
    const order: KycMockStep[] = [
      'intro',
      'consent',
      'documentType',
      'documentFront',
      'documentBack',
      'selfie',
    ];
    const idx = order.indexOf(this.step());
    if (idx > 0) {
      this.step.set(order[idx - 1]);
    }
  }

  cancel(): void {
    this.cancelled.emit();
  }
}
